import { useCallback, useMemo } from 'react';

import { RadioOptionsWithConfirm } from 'shared-components';
import { VisaStatus } from 'types';

import { IQuestion, IVisaStatus } from '../../../../../../../types';
import { SectionWrapper } from '../../../../../../atoms/section-wrapper';

interface IAnswer {
  status: VisaStatus;
  label: string;
  isSelected?: () => boolean;
}

interface IProps {
  visaStatus: IVisaStatus;
  selectedStatus: {
    status: IVisaStatus;
    setStatus: (status: IVisaStatus) => void;
  };
}

export const WhatIsYourVisaStatus = ({ selectedStatus, visaStatus }: IProps) => {
  const { status, setStatus } = selectedStatus;

  visaStatus = visaStatus || VisaStatus.NONE;

  const notHandledQuestions = useMemo(() => getArrayOfQuestions(visaStatus!), [visaStatus]);

  const getVisaStatusFromLabel = useCallback(
    (label: string) => {
      return notHandledQuestions.find(notHandledQuestion => {
        return notHandledQuestion.label === label;
      })!.status;
    },
    [notHandledQuestions]
  );

  const questions = useMemo(
    () =>
      notHandledQuestions.map((answer: IAnswer, index: number) => {
        return {
          char: (index + 1).toString(),
          label: answer.label,
          selected: answer.isSelected ? answer.isSelected() : status === getVisaStatusFromLabel(answer.label)
        };
      }),
    [notHandledQuestions, status, getVisaStatusFromLabel]
  );

  const clickHandler = useCallback(
    (question: IQuestion) => {
      const newStatus = getVisaStatusFromLabel(question.label);
      setStatus(newStatus);
      return true;
    },
    [getVisaStatusFromLabel, setStatus]
  );

  return (
    <SectionWrapper data-selector="enrolment-section-visa-status-questions">
      <RadioOptionsWithConfirm
        label={
          visaStatus === VisaStatus.NONE ? `Have you made a visa application?` : 'Please provide your update below'
        }
        questions={questions}
        onClick={clickHandler}
      />
    </SectionWrapper>
  );
};

const getArrayOfQuestions = (currentStatus: IVisaStatus): IAnswer[] => {
  const defaultStatuses: IAnswer[] = [
    {
      status: VisaStatus.INTENDS_TO_DEFER,
      label: 'I intend to defer to a later intake'
    },
    {
      status: VisaStatus.INTENDS_TO_WITHDRAW,
      label: 'I intend to withdraw my application '
    }
  ];

  const getAdditionalAnswers = (currentStatus: IVisaStatus): IAnswer[] => {
    switch (currentStatus) {
      case VisaStatus.NONE:
      case VisaStatus.CAS_RECEIVED:
      case VisaStatus.CAS_NOT_RECEIVED:
      case VisaStatus.INTENDS_TO_DEFER:
      case VisaStatus.INTENDS_TO_WITHDRAW: {
        return [
          {
            status: VisaStatus.VISA_APPLIED,
            label: 'I have applied for my visa'
          },
          {
            status: VisaStatus.CAS_RECEIVED,
            label: `I have my CAS, haven't applied for my visa`
          },
          {
            status: VisaStatus.CAS_NOT_RECEIVED,
            label: 'I have not received my CAS document'
          }
        ];
      }
      case VisaStatus.VISA_APPLIED:
      case VisaStatus.VISA_RECEIVED:
      case VisaStatus.VISA_REFUSAL:
      case VisaStatus.UKVI_REQUEST_MORE_INFO:
      case VisaStatus.AWAITING_VISA_RESPONSE: {
        return [
          {
            status: VisaStatus.VISA_RECEIVED,
            label: 'I have received my visa'
          },
          {
            status: VisaStatus.AWAITING_VISA_RESPONSE,
            label: `I am still waiting to hear back from UKVI`
          },
          {
            status: VisaStatus.UKVI_REQUEST_MORE_INFO,
            label: 'UKVI has requested more information'
          },
          {
            status: VisaStatus.VISA_REFUSAL,
            label: 'I received a visa refusal'
          }
        ];
      }

      default: {
        return [];
      }
    }
  };

  return [...getAdditionalAnswers(currentStatus), ...defaultStatuses].filter(({ status }) => status !== currentStatus);
};
